// Pure helpers for the Voice inspection settings tab (unit-tested in voice-utils.test.js).

export const DEFAULT_NOTE_LANGUAGE = 'en-US'

export const normalizeVoiceCompanySetting = res => {
    const s = res?.Setting || res?.setting || res || {}
    return {
        voiceEnabled: s.VoiceEnabled !== false,
        noteLanguage: s.NoteLanguage || DEFAULT_NOTE_LANGUAGE,
        keepAudio: !!s.KeepAudio,
        defaultHearLanguage: s.DefaultHearLanguage || '',
        isDefault: !!s.IsDefault,
        updatedOn: s.UpdatedOn || null,
    }
}

export const normalizeVoiceLanguages = res =>
    (res?.Languages || res?.languages || [])
        .filter(l => l && l.LanguageCode)
        .map(l => ({
            code: l.LanguageCode,
            englishName: l.EnglishName || l.LanguageCode,
            nativeName: l.NativeName || l.EnglishName || l.LanguageCode,
        }))

/** Dropdown options; `emptyText` adds a first "no choice" option (value ''). */
export const voiceLanguageOptions = (languages, emptyText) => [
    ...(emptyText ? [{ key: '', value: '', text: emptyText }] : []),
    ...languages.map(l => ({
        key: l.code,
        value: l.code,
        text:
            l.nativeName && l.nativeName !== l.englishName
                ? `${l.englishName} · ${l.nativeName}`
                : l.englishName,
    })),
]

export const normalizeVoiceTranslationsPage = res => ({
    totalCount: Number(res?.TotalCount) || 0,
    page: Number(res?.Page) || 1,
    size: Number(res?.Size) || 25,
    items: (res?.Items || []).map(r => ({
        id: r.TranslationId,
        targetLanguage: r.TargetLanguage || '',
        sourceText: r.SourceText || '',
        translatedText: r.TranslatedText || '',
        provider: r.Provider || '',
        isReviewed: !!r.IsReviewed,
        reviewedOn: r.ReviewedOn || null,
        createdOn: r.CreatedOn || null,
    })),
})

export const totalPages = (totalCount, size) =>
    Math.max(1, Math.ceil((Number(totalCount) || 0) / Math.max(Number(size) || 1, 1)))

/** reviewed filter: 'all' | 'unreviewed' | 'reviewed' -> undefined | false | true */
export const reviewedFilterValue = filter => {
    if (filter === 'reviewed') return true
    if (filter === 'unreviewed') return false
    return undefined
}

export const getVoiceApiErrorMessage = err =>
    err?.error?.Message ||
    err?.error?.message ||
    err?.message ||
    'Something went wrong. Please try again.'

// ---------------- Voice accuracy report ----------------

export const VOICE_ACCURACY_PERIODS = [7, 30, 90]

export const voiceAccuracyPeriodOptions = () =>
    VOICE_ACCURACY_PERIODS.map(d => ({ key: d, value: d, text: `Last ${d} days` }))

/** 8.25 -> "8.3%"; missing / not a number -> "0%". */
export const formatRate = value => {
    const n = Number(value)
    if (!Number.isFinite(n) || n <= 0) return '0%'
    const rounded = Math.round(n * 10) / 10
    return `${Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(1)}%`
}

/** Same rule as the server: misheard / (answered + misheard), one decimal. */
export const misheardRate = (answered, misheard) => {
    const a = Number(answered) || 0
    const m = Number(misheard) || 0
    return a + m > 0 ? Math.round((m * 1000) / (a + m)) / 10 : 0
}

/** The server answers 503 VOICE_LOG_NOT_INSTALLED until the database script has run. */
export const isVoiceLogNotInstalled = err =>
    err?.status === 503 || err?.error?.ErrorCode === 'VOICE_LOG_NOT_INSTALLED'

const num = v => Number(v) || 0

export const normalizeVoiceAccuracyReport = res => {
    const t = res?.Totals || {}
    return {
        days: num(res?.Days) || 30,
        totals: {
            sessions: num(t.Sessions),
            users: num(t.Users),
            questionsRead: num(t.QuestionsRead),
            answered: num(t.Answered),
            misheard: num(t.Misheard),
            confirms: num(t.Confirms),
            undos: num(t.Undos),
            instantAnswers: num(t.InstantAnswers),
            misheardRate: num(t.MisheardRate),
            undoRate: num(t.UndoRate),
            readThenListenSessions: num(t.ReadThenListenSessions),
        },
        byUser: (res?.ByUser || []).map(u => ({
            key: `u${u.UserId}`,
            name: u.Name || `User #${u.UserId}`,
            sessions: num(u.Sessions),
            answered: num(u.Answered),
            misheard: num(u.Misheard),
            undos: num(u.Undos),
            misheardRate: num(u.MisheardRate),
            undoRate: num(u.UndoRate),
            lastUsedOnUtc: u.LastUsedOnUtc || null,
        })),
        byTemplate: (res?.ByTemplate || []).map(t2 => ({
            key: `t${t2.TemplateId}`,
            name: t2.TemplateName || `Template #${t2.TemplateId}`,
            sessions: num(t2.Sessions),
            answered: num(t2.Answered),
            misheard: num(t2.Misheard),
            undos: num(t2.Undos),
            misheardRate: num(t2.MisheardRate),
            undoRate: num(t2.UndoRate),
        })),
        questions: (res?.Questions || []).map((q, i) => ({
            key: `q${i}-${q.TemplateId || 0}-${q.TemplateQuestionId || 0}`,
            text: q.QuestionText || '(no text)',
            templateName: q.TemplateName || '',
            sessions: num(q.Sessions),
            misses: num(q.Misses),
            confirms: num(q.Confirms),
            undos: num(q.Undos),
        })),
        languages: (res?.Languages || []).map((l, i) => ({
            key: `l${i}-${l.Language || ''}`,
            language: l.Language || 'unknown',
            sessions: num(l.Sessions),
            misheardRate: num(l.MisheardRate),
        })),
    }
}
